/** @jsxImportSource @emotion/react */

import {
  formatErrorResponse,
  useSession,
} from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Typography,
  useTheme,
  DateInput,
  Table,
  Icon,
  Input,
  Pagination,
  UtilityButton,
  List,
  SearchInput,
  Modal,
  UtilitySelect,
  Option,
  Copy,
  Snackbar,
  Tooltip,
} from '@quickbit/qb-design-system';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync, useAsyncFn, useDebounce } from 'react-use';
import { JsonView, QBLoader, TruncatedString } from 'components';
import { useOrganisation } from 'contexts';
import {
  ORDER_STATUS_OPTIONS,
  Order,
  OrderCallback,
  OrderError,
  OrderStatus,
  OrderType,
} from 'tabs/types';
import { MerchantOrderFilters, SortOptions, getConfig } from '../api';
import { getOrderOverview, refundOrder, RefundOrderResponse } from './api';
import { ActionsDropdown, OrderDetails, DownloadModal } from './components';
import { CreateOrderModal } from './components/CreateOrderModal';
import { createReport } from './utils';

const PAGE_SIZE = 16;

type CellKey =
  | 'createdAt'
  | 'orderId'
  | 'referenceId'
  | 'amount'
  | 'processingFee'
  | 'status'
  | 'merchantName'
  | 'actions';

const getHeaders = (
  withActions: boolean
): { label: string; cellKey: CellKey; sort?: boolean }[] => [
  { label: 'Timestamp', cellKey: 'createdAt' },
  { label: 'Merchant', cellKey: 'merchantName', sort: false },
  { label: 'Order ID', cellKey: 'orderId' },
  { label: 'Reference ID', cellKey: 'referenceId' },
  { label: 'Amount', cellKey: 'amount' },
  { label: 'Fee', cellKey: 'processingFee' },
  { label: 'Status', cellKey: 'status', sort: false },
  ...(withActions
    ? [{ label: 'Actions', cellKey: 'actions' as CellKey, sort: false }]
    : []),
];

const CELL_KEY_TO_SORT_FIELD: Record<CellKey, SortOptions['field']> = {
  createdAt: 'CREATED_AT',
  orderId: 'ORDER_ID',
  referenceId: 'REFERENCE_ID',
  amount: 'AMOUNT',
  processingFee: 'PROCESSING_FEE',
  status: 'STATUS',
  actions: 'STATUS',
  merchantName: 'STATUS',
};

const ORDER_STATUS_OPTIONS_TRANSFORMED = ORDER_STATUS_OPTIONS.map((s) => ({
  label: s,
  value: s,
}));
const ORDER_TYPE_FILTER_OPTIONS = ['PAY', 'ONRAMP', 'PROCESSING'].map((s) => ({
  value: s,
  label: _.capitalize(s),
}));
const ORDER_ABANDONED_FILTER_OPTIONS = ['Is abandoned'].map((s) => ({
  value: s,
  label: s,
}));
const ERROR_FILTER_OPTIONS = ['With errors', 'With no errors'].map((s) => ({
  value: s,
  label: s,
}));
const HAS_FAILED_CALLBACK_OPTIONS = ['With failed callback'].map((s) => ({
  value: s,
  label: s,
}));
interface SnackbarInfo {
  variant: 'success' | 'danger';
  message: string | string[];
}

export const Orders = () => {
  const session = useSession();
  const { spacing, palette, sizing } = useTheme();
  const [dateFilterRange, setDateFilterRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [statusFilter, setStatusFilter] = useState<Option[]>([]);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [isRefundAmountTooLarge, setIsRefundAmountTooLarge] = useState(false);
  const [abandonedAfterBuy, setAbandonedAfterBuy] = useState<Option | null>(
    null
  );
  const [sortOptions, setSortOptions] = useState<SortOptions>();
  const [searchInput, setSearchInput] = useState<string>('');
  const [maxToRefund, setMaxToRefund] = useState<number | null>();
  const [searchByReferenceIdInput, setSearchByReferenceIdInput] =
    useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchByReferenceIdQuery, setSearchByReferenceIdQuery] =
    useState<string>('');
  const [orderType, setOrderType] = useState<Option | null>(null);
  const [orderErrors, setOrderErrors] = useState<OrderError[] | null>(null);
  const [orderCallbacks, setOrderCallbacks] = useState<{
    callbacks: OrderCallback[];
    url: string | null;
  } | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [refundData, setRefundData] = useState<RefundOrderResponse>();
  const [selectedRefundOrder, setSelectedRefundOrder] = useState<Order | null>(
    null
  );
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [errorFilter, setErrorFilter] = useState<Option | null>(null);
  const [hasFailedCallback, setHasFailedCallback] = useState<Option | null>(
    null
  );
  const [merchant, setMerchant] = useState<Option | null>(null);
  const organisation = useOrganisation();
  const [snackbarInfo, setSnackbarInfo] = useState<SnackbarInfo>();

  const { value: config } = useAsync(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!organisation) return;
    if (organisation.isPlatform) {
      return;
    }
    const configResponse = await getConfig();
    return configResponse.config;
  }, [organisation]);

  const actualFilters: MerchantOrderFilters = useMemo(() => {
    return {
      createdFrom: dateFilterRange[0],
      createdTo: dateFilterRange[1],
      externalStatuses: statusFilter.map((v) => v.value) as OrderStatus[],
      orderOrPreviewId: searchQuery,
      referenceId: searchByReferenceIdQuery,
      orderType: orderType?.value as OrderType,
      errors: errorFilter ? errorFilter.value === 'With errors' : null,
      merchantId: merchant?.value,
      hasFailedCallback: hasFailedCallback ? true : null,
      abandonedAfterBuy: abandonedAfterBuy ? true : null,
      completedFrom: null,
      completedTo: null,
    } satisfies MerchantOrderFilters;
  }, [
    dateFilterRange,
    statusFilter,
    searchQuery,
    searchByReferenceIdQuery,
    orderType,
    errorFilter,
    abandonedAfterBuy,
    hasFailedCallback,
    merchant,
  ]);

  const [{ value, loading }, refreshOrders] = useAsyncFn(
    () =>
      getOrderOverview({
        pagination: { size: PAGE_SIZE, page },
        filters: actualFilters,
        sortOptions,
      }),
    [page, sortOptions, actualFilters]
  );

  const [
    { value: refundValue, error: refundError, loading: refundOrderLoading },
    onRefundOrder,
  ] = useAsyncFn(async () => {
    if (!selectedRefundOrder || !refundAmount) return;
    const data = await refundOrder(selectedRefundOrder.uuid, refundAmount);
    await refreshOrders();
    if (data.refundUrl) {
      setRefundData(data);
    }
    return data;
  }, [selectedRefundOrder, refundAmount, refreshOrders]);

  useEffect(() => {
    if (refundOrderLoading) {
      setSnackbarInfo(undefined);
    } else if (refundError) {
      setSnackbarInfo({
        variant: 'danger',
        message: formatErrorResponse(refundError),
      });
    } else if (refundValue && !refundValue.refundUrl) {
      setSnackbarInfo({
        variant: 'success',
        message: `Refund successful!`,
      });
    } else if (refundData?.refundUrl) {
      setSnackbarInfo({
        variant: 'success',
        message: `Refund initiated succesfully!`,
      });
    }
  }, [
    refundError,
    refundOrderLoading,
    setSnackbarInfo,
    refundData,
    refundValue,
  ]);

  const handleSort = useCallback(
    (cellKey: string, asc: boolean) => {
      setSortOptions({
        field: CELL_KEY_TO_SORT_FIELD[cellKey as CellKey],
        ascending: asc,
      });
    },
    [setSortOptions]
  );

  const handleResetFilters = useCallback(() => {
    setDateFilterRange([null, null]);
    setAbandonedAfterBuy(null);
    setStatusFilter([]);
    setOrderType(null);
    setErrorFilter(null);
    setMerchant(null);
    setHasFailedCallback(null);
  }, [setDateFilterRange, setStatusFilter]);

  const handleRefundClick = async () => {
    const response = await onRefundOrder();
    if (response && !response.refundUrl) {
      setSelectedRefundOrder(null);
    }
  };

  useDebounce(
    () => {
      setPage(1);
      setSearchQuery(searchInput);
      setSearchByReferenceIdQuery(searchByReferenceIdInput);
    },
    500,
    [searchInput, searchByReferenceIdInput]
  );

  useEffect(() => {
    void refreshOrders();
  }, [refreshOrders]);

  const headers = getHeaders(
    session.authenticated && session.permissions.includes('edit_orders')
  );

  const rows = useMemo(
    () =>
      value?.orders.map((order) => ({
        value: {
          createdAt: order.createdAt.toLocaleString(),
          orderId: <TruncatedString text={order.uuid} />,
          referenceId: order.referenceId ?? undefined,
          amount: `${order.creationAmount.toString()} ${order.currency}`,
          processingFee: order.settlement?.euroFee
            ? `€ ${order.settlement.euroFee.toString()}`
            : '',
          status: order.externalStatus.replace(/_/g, ' '),
          merchantName: (
            <div css={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="bodySmall">{order.merchant.name}</Typography>
              <Copy textToCopy={order.merchant.uuid} />
            </div>
          ),
          actions: (
            <ActionsDropdown
              key={order.uuid}
              order={order}
              refresh={refreshOrders}
              openErrorModal={() => {
                setOrderErrors(order.errors);
              }}
              openCallbackModal={() => {
                setOrderCallbacks({
                  callbacks: order.callbacks,
                  url: order.callbackUrl,
                });
              }}
              openRefundOrderModal={() => {
                setSelectedRefundOrder(order);
                setRefundAmount(order.creationAmount);
                setMaxToRefund(order.creationAmount);
              }}
              openDetailsModel={() => {
                setSelectedOrder(order);
              }}
            />
          ),
        },
      })),
    [value, refreshOrders]
  );

  const handleDownload = useCallback(
    async (includeIncomplete: boolean) => {
      const reportFilters = { ...actualFilters };
      if (!includeIncomplete) {
        reportFilters.completedFrom = actualFilters.createdFrom;
        reportFilters.completedTo = actualFilters.createdTo;
        reportFilters.createdFrom = null;
        reportFilters.createdTo = null;
        reportFilters.hadExternalStatuses = ['COMPLETED'];
      }
      setIsDownloading(true);
      await createReport({
        filters: reportFilters,
        includeIncomplete,
      });
      setShowDownloadModal(false);
      setIsDownloading(false);
    },
    [setShowDownloadModal, actualFilters]
  );

  if (!value) return <QBLoader />;

  const { isPlatform, merchants } = organisation;
  const merchantsOptions = merchants.map(({ merchantId, name }) => ({
    value: merchantId,
    label: name,
  }));
  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateRows: 'auto auto auto 1fr auto',
          height: '100%',
        }}
      >
        {snackbarInfo && (
          <Snackbar
            variant={snackbarInfo.variant}
            message={snackbarInfo.message}
            open={!!snackbarInfo}
            onClose={() => {
              setSnackbarInfo(undefined);
            }}
          />
        )}
        <Card>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div css={{ flex: 1 }}>
              <Typography variant="subtitle">Orders</Typography>
            </div>
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: spacing.xxs,
                alignItems: 'center',
              }}
            >
              <Tooltip
                title={
                  actualFilters.createdFrom === null
                    ? 'Select a date range to enable downloading'
                    : 'Download report of selected orders'
                }
              >
                <UtilityButton
                  text="Download"
                  onClick={() => {
                    setShowDownloadModal(true);
                  }}
                  loading={isDownloading}
                  disabled={isDownloading || actualFilters.createdFrom === null}
                />
              </Tooltip>
              {config?.allowBackofficeOrderCreation && (
                <UtilityButton
                  text="Create Order"
                  css={{
                    whiteSpace: 'nowrap',
                    padding: '18px',
                  }}
                  onClick={() => {
                    setShowCreateOrder(true);
                  }}
                />
              )}

              {/* Hide p in search inputs since the not used helper text is ruining the layout */}
              <SearchInput
                css={{ p: { display: 'none' } }}
                placeholder="Order ID or Receive Preview ID"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
              />
              <SearchInput
                css={{ p: { display: 'none' } }}
                placeholder="Reference ID"
                value={searchByReferenceIdInput}
                onChange={(e) => {
                  setSearchByReferenceIdInput(e.target.value);
                }}
              />
            </div>
          </div>
        </Card>
        <div
          css={{
            marginTop: spacing.xxs,
            display: 'grid',
            gridTemplateColumns: 'repeat(7, auto) 1fr',
            gap: spacing.xxxs,
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          <DateInput
            placeholder="All time"
            type="range"
            value={dateFilterRange}
            onChange={(e: [Date | null, Date | null]) => {
              setPage(1);
              setDateFilterRange(e);
            }}
            withTime
          />
          <UtilitySelect
            label={
              statusFilter.length > 0
                ? `Statuses (${statusFilter.length.toString()})`
                : 'All statuses'
            }
            listTitle="Status"
            multiple={true}
            options={ORDER_STATUS_OPTIONS_TRANSFORMED}
            value={statusFilter}
            onSelect={(data) => {
              setPage(1);
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              setStatusFilter((data as Option[]) || []);
            }}
          />

          <UtilitySelect
            label={orderType ? _.capitalize(orderType.value) : 'Order type'}
            listTitle="Order type"
            options={ORDER_TYPE_FILTER_OPTIONS}
            value={orderType}
            onSelect={(data) => {
              setPage(1);
              setOrderType(data as Option);
            }}
          />
          <UtilitySelect
            label={abandonedAfterBuy ? 'Is Abandoned' : 'Abandoned'}
            listTitle="Abandoned orders"
            options={ORDER_ABANDONED_FILTER_OPTIONS}
            value={abandonedAfterBuy}
            onSelect={(data) => {
              setPage(1);
              setAbandonedAfterBuy(data as Option);
            }}
          />
          <UtilitySelect
            label={errorFilter === null ? 'Errors' : errorFilter.label}
            listTitle="Order errors"
            options={ERROR_FILTER_OPTIONS}
            value={errorFilter}
            onSelect={(data) => {
              setPage(1);
              setErrorFilter(data as Option);
            }}
          />
          <UtilitySelect
            label={
              hasFailedCallback === null
                ? 'Failed callback'
                : hasFailedCallback.label
            }
            listTitle="Failed callback"
            options={HAS_FAILED_CALLBACK_OPTIONS}
            value={hasFailedCallback}
            onSelect={(data) => {
              setPage(1);
              setHasFailedCallback(data as Option);
            }}
          />
          {isPlatform && (
            <UtilitySelect
              label={merchant ? _.capitalize(merchant.label) : 'Merchant'}
              listTitle="Merchants"
              options={merchantsOptions.length > 0 ? merchantsOptions : []}
              value={merchant}
              onSelect={(m) => {
                setMerchant(m as Option);
              }}
              searchable={true}
            />
          )}
          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
          <UtilityButton
            variant="secondary"
            text="Refresh"
            onClick={refreshOrders}
            disabled={loading}
          />
          {loading && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Table headers={headers} rows={rows ?? []} onSort={handleSort} />
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Pagination
            currentPage={page}
            onChange={setPage}
            numberOfElements={value.aggregatedData.orderCount}
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>
      {orderErrors && (
        <Modal
          title="Order errors"
          secondaryButtonProps={{
            onClick: () => {
              setOrderErrors(null);
            },
            text: 'Close',
          }}
          closeModal={() => {
            setOrderErrors(null);
          }}
        >
          <div
            css={{
              overflow: 'scroll',
              maxHeight: '80vh',
              minWidth: sizing.medium,
            }}
          >
            <List>
              {orderErrors.map((e, i) => (
                <div key={i} css={{ padding: spacing.xxs }}>
                  <Typography variant="bodySmallBold">{e.errorCode}</Typography>
                  <Typography variant="bodySmallest">
                    {`At ${e.createdAt.toLocaleString()}`}
                  </Typography>
                  <JsonView data={e.details} />
                </div>
              ))}
            </List>
          </div>
        </Modal>
      )}
      {orderCallbacks && (
        <Modal
          title="Order callbacks"
          secondaryButtonProps={{
            onClick: () => {
              setOrderCallbacks(null);
            },
            text: 'Close',
          }}
          closeModal={() => {
            setOrderCallbacks(null);
          }}
        >
          <Typography variant="bodySmallest">{orderCallbacks.url}</Typography>
          <div
            css={{
              overflow: 'scroll',
              maxHeight: '80vh',
              minWidth: sizing.medium,
            }}
          >
            <List>
              {orderCallbacks.callbacks.map((c, i) => (
                <div key={i} css={{ padding: spacing.xxs }}>
                  <span
                    css={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography
                      variant="bodySmallBold"
                      color={
                        c.ok === null
                          ? undefined
                          : c.ok
                            ? palette.success.x500
                            : palette.danger.x500
                      }
                    >
                      {c.ok === null
                        ? 'Processing'
                        : c.ok
                          ? 'Success'
                          : 'Failed'}
                    </Typography>
                    <Typography variant="bodySmallest">
                      {`At ${c.createdAt.toLocaleString()}`}
                    </Typography>
                  </span>
                  <Typography variant="bodySmall">Request body:</Typography>
                  <JsonView data={c.requestBody} />
                  {c.responseContent && (
                    <>
                      <Typography variant="bodySmall">Response:</Typography>
                      <pre>{c.responseContent}</pre>
                    </>
                  )}
                </div>
              ))}
            </List>
          </div>
        </Modal>
      )}
      {selectedOrder && (
        <OrderDetails
          order={selectedOrder}
          deselect={() => {
            setSelectedOrder(null);
          }}
          allowBackofficeOrderCreation={config?.allowBackofficeOrderCreation}
        />
      )}
      {selectedRefundOrder && (
        <Modal
          title="Refund order"
          primaryButtonProps={{
            disabled:
              isRefundAmountTooLarge ||
              refundOrderLoading ||
              !!refundData?.refundUrl,
            loading: refundOrderLoading,
            onClick: handleRefundClick,
            text: 'Refund',
          }}
          secondaryButtonProps={{
            onClick: () => {
              setSelectedRefundOrder(null);
              setRefundData(undefined);
            },
            text: !refundData ? 'Cancel' : 'Close',
          }}
          closeModal={() => () => {
            setSelectedRefundOrder(null);
            setRefundData(undefined);
          }}
        >
          {!refundData && (
            <Input
              label="Amount"
              type="number"
              endAdornment={selectedRefundOrder.currency}
              value={refundAmount ? refundAmount.toString() : ''}
              error={isRefundAmountTooLarge}
              helperText={
                isRefundAmountTooLarge ? 'The amount is too high' : ''
              }
              onChange={(e) => {
                const value = Number(e.currentTarget.value);
                setRefundAmount(value);
                if (maxToRefund && value <= maxToRefund) {
                  setIsRefundAmountTooLarge(false);
                } else {
                  setIsRefundAmountTooLarge(true);
                }
              }}
            />
          )}
          {refundData?.refundUrl && (
            <>
              <Typography>
                Copy and provide this link to the user for them to get a refund.
              </Typography>
              <div
                css={{
                  background: palette.primary.x50,
                  color: palette.primary.x700,
                  border: `1px solid ${palette.primary.x200}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  padding: spacing.xxs,
                  marginTop: spacing.xxs,
                }}
              >
                <Typography variant="bodySmall">
                  {refundData.refundUrl}
                </Typography>
                <Copy textToCopy={refundData.refundUrl} />
              </div>
            </>
          )}
        </Modal>
      )}
      {showCreateOrder && (
        <CreateOrderModal
          onSuccess={async () => {
            setShowCreateOrder(false);
            await refreshOrders();
          }}
          onClose={() => {
            setShowCreateOrder(false);
          }}
        />
      )}
      {showDownloadModal && (
        <DownloadModal
          onCreate={handleDownload}
          onCancel={() => {
            setShowDownloadModal(false);
          }}
          from={actualFilters.createdFrom}
          to={actualFilters.createdTo}
          isDownloading={isDownloading}
        />
      )}
    </>
  );
};
